/**
 * Livacils Cart Drawer
 * Shopify AJAX Cart API integration
 */

(function () {
  'use strict';

  const SHIPPING_THRESHOLD = (window.cartSettings?.freeShippingThreshold || 40) * 100;

  let cartState = null;
  let isOpen = false;
  let crossSellIndex = 0;

  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  const panel = drawer.querySelector('.cart-drawer__panel');
  const itemsContainer = drawer.querySelector('[data-cart-items]');
  const subtotalEl = drawer.querySelector('[data-cart-subtotal]');
  const countEls = document.querySelectorAll('[data-cart-count]');
  const shippingFill = drawer.querySelector('[data-shipping-fill]');
  const shippingText = drawer.querySelector('[data-shipping-text]');
  const crossSellSection = drawer.querySelector('[data-cross-sell-section]');
  const crossSellList = drawer.querySelector('[data-cross-sell]');
  const crossSellTitle = drawer.querySelector('.cart-drawer__cross-sell-title');

  let crossSellPool = [];
  try {
    const dataEl = drawer.querySelector('[data-cross-sell-data]');
    if (dataEl) {
      crossSellPool = JSON.parse(dataEl.textContent.trim()).filter(Boolean);
    }
  } catch (err) {
    console.warn('Cross-sell data parse error:', err);
  }

  function openDrawer() {
    isOpen = true;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    panel?.querySelector('.cart-drawer__close')?.focus();
    fetchCart();
  }

  function closeDrawer() {
    isOpen = false;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  document.querySelectorAll('[data-cart-open]').forEach((btn) => {
    btn.addEventListener('click', openDrawer);
  });
  document.querySelectorAll('[data-cart-close]').forEach((btn) => {
    btn.addEventListener('click', closeDrawer);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen) closeDrawer();
  });

  async function fetchCart() {
    try {
      const res = await fetch('/cart.js', {
        headers: { 'Content-Type': 'application/json' },
      });
      cartState = await res.json();
      renderCart();
    } catch (err) {
      console.error('Cart fetch error:', err);
    }
  }

  async function addToCart(variantId, quantity = 1, properties = {}) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity, properties }),
      });
      if (!res.ok) throw new Error('Add to cart failed');
      openDrawer();
    } catch (err) {
      console.error('Add to cart error:', err);
      window.showToast?.('Erreur lors de l\'ajout au panier');
    }
  }

  async function updateCart(key, quantity) {
    drawer.classList.add('cart-drawer--loading');
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      cartState = await res.json();
      renderCart();
    } catch (err) {
      console.error('Cart update error:', err);
    } finally {
      drawer.classList.remove('cart-drawer--loading');
    }
  }

  function renderCart() {
    if (!cartState) return;

    updateCountBadges(cartState.item_count);
    updateShippingProgress(cartState.total_price);

    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(cartState.total_price);
    }

    if (!itemsContainer) return;

    if (cartState.item_count === 0) {
      itemsContainer.innerHTML = `
        <div class="cart-drawer__empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" x2="21" y1="6" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
          <p>Ton panier est vide</p>
          <a href="/collections/all" class="btn btn-ghost btn-sm" onclick="document.querySelector('[data-cart-close]').click()">
            Continuer mes achats
          </a>
        </div>
      `;
      renderCrossSell();
      return;
    }

    itemsContainer.innerHTML = cartState.items.map(renderCartItem).join('');
    bindCartItemEvents();
    renderCrossSell();
  }

  function renderCartItem(item) {
    const imgSrc = item.image
      ? item.image.replace('.jpg', '_120x120.jpg').replace('.png', '_120x120.png')
      : '';

    return `
      <div class="cart-item" data-cart-item data-key="${item.key}">
        ${imgSrc ? `<img src="${imgSrc}" alt="${escapeHtml(item.title)}" class="cart-item__image" width="88" height="88" loading="lazy">` : '<div class="cart-item__image" style="background:var(--color-nude)"></div>'}
        <div class="cart-item__details">
          <p class="cart-item__title">${escapeHtml(item.product_title)}</p>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="cart-item__variant">${escapeHtml(item.variant_title)}</p>` : ''}
          <p class="cart-item__price">${formatMoney(item.final_line_price)}</p>
          <div class="cart-item__actions">
            <div class="qty-stepper" aria-label="Quantité">
              <button class="qty-stepper__btn" data-qty-decrease aria-label="Diminuer">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" x2="19" y1="12" y2="12"/></svg>
              </button>
              <input type="number" class="qty-stepper__value" value="${item.quantity}" min="0" max="99" data-qty-input aria-label="Quantité">
              <button class="qty-stepper__btn" data-qty-increase aria-label="Augmenter">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" x2="12" y1="5" y2="19"/><line x1="5" x2="19" y1="12" y2="12"/></svg>
              </button>
            </div>
            <button class="cart-item__remove" data-item-remove aria-label="Supprimer ${escapeHtml(item.product_title)}">
              Supprimer
            </button>
          </div>
        </div>
      </div>
    `;
  }

  function bindCartItemEvents() {
    drawer.querySelectorAll('[data-cart-item]').forEach((itemEl) => {
      const key = itemEl.dataset.key;
      const input = itemEl.querySelector('[data-qty-input]');
      const decrease = itemEl.querySelector('[data-qty-decrease]');
      const increase = itemEl.querySelector('[data-qty-increase]');
      const remove = itemEl.querySelector('[data-item-remove]');

      decrease?.addEventListener('click', () => {
        const qty = parseInt(input.value, 10);
        updateCart(key, Math.max(0, qty - 1));
      });
      increase?.addEventListener('click', () => {
        const qty = parseInt(input.value, 10);
        updateCart(key, qty + 1);
      });
      input?.addEventListener('change', () => {
        updateCart(key, Math.max(0, parseInt(input.value, 10) || 0));
      });
      remove?.addEventListener('click', () => updateCart(key, 0));
    });
  }

  // ─── Cross-sell v4 : flèches sur les côtés du card ───────────────
  function renderCrossSell() {
    if (!crossSellSection || !crossSellList) return;
    if (crossSellPool.length === 0) {
      crossSellSection.style.display = 'none';
      return;
    }

    const inCartIds = new Set(
      (cartState?.items || []).map((item) => item.product_id)
    );
    const available = crossSellPool.filter((p) => p && !inCartIds.has(p.id));

    if (available.length === 0) {
      crossSellSection.style.display = 'none';
      return;
    }

    if (crossSellIndex >= available.length) crossSellIndex = 0;
    if (crossSellIndex < 0) crossSellIndex = available.length - 1;

    const current = available[crossSellIndex];
    crossSellSection.style.display = 'block';

    // Titre simple, centré (pas de flèches inline)
    if (crossSellTitle) {
      crossSellTitle.classList.remove('cart-drawer__cross-sell-title--with-nav');
      crossSellTitle.innerHTML = 'Se marie parfaitement avec';
    }

    // Build le carrousel : ‹ + carte + ›
    const showNav = available.length > 1;
    crossSellList.innerHTML = `
      ${showNav ? `
        <button class="cross-sell-nav" data-cross-sell-prev aria-label="Suggestion précédente" type="button">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
      ` : ''}
      ${renderCrossSellItem(current)}
      ${showNav ? `
        <button class="cross-sell-nav" data-cross-sell-next aria-label="Suggestion suivante" type="button">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      ` : ''}
    `;

    bindCrossSellEvents();
    bindCrossSellNav(available);
  }

  function renderCrossSellItem(p) {
    const imgSrc = p.image || '';
    return `
      <div class="cross-sell-item" data-cross-sell-item>
        ${imgSrc ? `<img src="${imgSrc}" alt="${escapeHtml(p.title)}" class="cross-sell-item__image" width="64" height="64" loading="lazy">` : '<div class="cross-sell-item__image" style="background:var(--color-nude)"></div>'}
        <div class="cross-sell-item__info">
          <a href="${p.url}" class="cross-sell-item__title" title="${escapeHtml(p.title)}">${escapeHtml(p.title)}</a>
          <p class="cross-sell-item__price">${p.price_formatted}</p>
        </div>
        <button
          class="cross-sell-item__add"
          data-cross-sell-add="${p.variant_id}"
          aria-label="Ajouter ${escapeHtml(p.title)} au panier"
          type="button"
        >Ajouter</button>
      </div>
    `;
  }

  function bindCrossSellEvents() {
    drawer.querySelectorAll('[data-cross-sell-add]').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const variantId = btn.dataset.crossSellAdd;
        if (!variantId) return;
        btn.disabled = true;
        const originalText = btn.textContent;
        btn.textContent = '...';
        await addToCart(variantId, 1);
        await fetchCart();
        btn.disabled = false;
        btn.textContent = originalText;
      });
    });
  }

  function bindCrossSellNav(available) {
    const prevBtn = drawer.querySelector('[data-cross-sell-prev]');
    const nextBtn = drawer.querySelector('[data-cross-sell-next]');

    prevBtn?.addEventListener('click', () => {
      crossSellIndex = (crossSellIndex - 1 + available.length) % available.length;
      renderCrossSell();
    });
    nextBtn?.addEventListener('click', () => {
      crossSellIndex = (crossSellIndex + 1) % available.length;
      renderCrossSell();
    });
  }

  function updateCountBadges(count) {
    countEls.forEach((el) => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  function updateShippingProgress(totalPrice) {
    if (!shippingFill || !shippingText) return;
    const remaining = Math.max(0, SHIPPING_THRESHOLD - totalPrice);
    const pct = Math.min(100, (totalPrice / SHIPPING_THRESHOLD) * 100);
    shippingFill.style.width = `${pct}%`;
    if (remaining === 0) {
      shippingText.innerHTML = '<strong>Tu as la livraison offerte !</strong> 🎉';
    } else {
      shippingText.innerHTML = `Plus que <strong>${formatMoney(remaining)}</strong> pour la livraison offerte`;
    }
  }

  document.querySelectorAll('[data-atc-form]').forEach((form) => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const variantId = form.querySelector('[name="id"]')?.value;
      const qty = parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10);
      if (!variantId) return;
      const btn = form.querySelector('[data-add-to-cart]');
      if (btn) { btn.disabled = true; btn.textContent = 'Ajout...'; }
      await addToCart(variantId, qty);
      if (btn) { btn.disabled = false; btn.textContent = 'Ajouter au panier'; }
    });
  });

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    const variantId = btn.dataset.quickAdd;
    if (!variantId) return;
    btn.textContent = '...';
    btn.disabled = true;
    await addToCart(variantId, 1);
    btn.textContent = 'Ajout rapide';
    btn.disabled = false;
  });

  function formatMoney(cents) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(cents / 100);
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  fetchCart();
  window.LiveCart = { addToCart, fetchCart, openDrawer, closeDrawer };
})();
